import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Users, Award, Heart } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Quality Workmanship',
      description: 'Every project is completed to the highest standards. We don\'t cut corners.',
    },
    {
      icon: Users,
      title: 'Clear Communication',
      description: 'Regular updates and transparent processes from start to finish.',
    },
    {
      icon: Award,
      title: 'Reliability',
      description: 'We show up on time, meet deadlines, and honor our commitments.',
    },
    {
      icon: Heart,
      title: 'Local Expertise',
      description: 'As Calgary locals, we understand the unique needs of Alberta homes.',
    },
  ];

  const stats = [
    { value: '500+', label: 'Projects Completed' },
    { value: '15+', label: 'Years Experience' },
    { value: '100%', label: 'Satisfaction Rate' },
    { value: '5 Star', label: 'Google Rating' },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1665186898214-3881adcc0e15?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="About YYC Custom Basement & Bath"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0f172a]/70" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
            About Us
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
            Built Right. Built to Last.
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Calgary's trusted renovation experts, delivering quality craftsmanship 
            and reliable service for over 15 years.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
                Our Story
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-6">
                Local Calgary Contractor, Professional Results
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed">
                YYC Custom Basement & Bath was founded with a simple mission: deliver 
                exceptional renovation work with honest, transparent service. As a locally 
                owned and operated company, we take pride in improving homes across Calgary 
                and the surrounding communities.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our team brings together skilled tradespeople with years of experience in 
                basement development, bathroom renovations, and custom finishing work. We 
                understand that your home is your biggest investment, and we treat every 
                project with the care and attention it deserves.
              </p>
              <p className="text-slate-600 leading-relaxed">
                From the initial consultation to the final walkthrough, we're committed to 
                exceeding your expectations with quality workmanship and clear communication 
                every step of the way.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1704040686294-9c1878cf5c7d?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Completed basement"
                className="w-full h-48 md:h-64 object-cover"
              />
              <img
                src="https://images.unsplash.com/photo-1758548157466-7c454382035a?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Completed bathroom"
                className="w-full h-48 md:h-64 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} data-testid={`about-stat-${index}`}>
                <p className="font-heading text-4xl md:text-5xl font-bold text-[#c5a059]">
                  {stat.value}
                </p>
                <p className="text-slate-300 text-sm uppercase tracking-wider mt-2">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-[#f8f9fa]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              What Sets Us Apart
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
              Our Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-8 text-center hover:-translate-y-1 transition-transform"
                data-testid={`about-value-${index}`}
              >
                <value.icon size={40} className="text-[#c5a059] mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
                Why Choose Us
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-8">
                The YYC Custom Difference
              </h2>
              <div className="space-y-6">
                {[
                  'Fully licensed and insured in Alberta',
                  'All permits handled and inspections arranged',
                  'Transparent pricing with no hidden costs',
                  'Clear project timelines and regular updates',
                  'Quality materials and professional installation',
                  'Warranty on all workmanship',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle size={24} className="text-[#c5a059] flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#f8f9fa] p-8 md:p-12">
              <h3 className="font-heading text-2xl font-semibold text-[#0f172a] mb-4">
                Serving Calgary & Area
              </h3>
              <p className="text-slate-600 mb-6">
                We proudly serve homeowners throughout Calgary and the surrounding 
                communities, including:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['Calgary', 'Airdrie', 'Cochrane', 'Chestermere', 'Okotoks'].map((area) => (
                  <div key={area} className="flex items-center gap-2 text-slate-600">
                    <div className="w-2 h-2 bg-[#c5a059] rounded-full" />
                    <span>{area}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Get in touch for a free, no-obligation estimate. We'd love to discuss 
            your renovation project.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
            data-testid="about-cta-button"
          >
            Request Free Estimate
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
